/**
 * Branch picker for the top app bar — modelled on the backend.html store picker.
 *
 * Only renders on store-scoped routes (Live Orders, for now), so it doesn't sit
 * there implying a filter on pages where it does nothing.
 *
 * The options are the signed-in user's active stores from the database
 * (GET /admin/stores/options) — every active store for head office, only the
 * assigned ones for store roles. Each option is picked by its id (externalId),
 * which is what the orders filter matches on.
 */

import React, { useEffect, useState } from 'react';
import { Box, Menu, MenuItem, ListItemText, Divider, CircularProgress } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useLocation } from 'react-router-dom';
import { useStoreScope, useEffectiveStore, isStoreScopedRoute, ALL_STORES, NO_STORE } from '../context/StoreScopeContext';

export default function StorePicker() {
  const { pathname } = useLocation();
  const { setStore, stores, storesStatus, requestStores } = useStoreScope();
  const store = useEffectiveStore();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const scoped = isStoreScopedRoute(pathname);

  // Load the options once the picker is actually showing (the provider sits
  // above sign-in, so it can't fetch on mount). A no-op once loaded.
  useEffect(() => {
    if (scoped) requestStores();
  }, [scoped, pathname, requestStores]);

  // Hidden wherever the scope has no meaning.
  if (!scoped) return null;

  const loading = storesStatus === 'loading' || storesStatus === 'idle';
  const selectedName = stores.find((s) => s.externalId === store)?.name;
  const label = store === NO_STORE
    ? 'Select'
    : store === ALL_STORES
      ? 'All stores'
      : selectedName ?? (loading ? 'Loading stores…' : 'Select');

  const choose = (value: string) => {
    setStore(value);
    setAnchor(null);
  };

  return (
    <>
      <Box
        role="button"
        aria-label="Store"
        aria-disabled={loading}
        onClick={(e) => { if (!loading) setAnchor(e.currentTarget); }}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          cursor: loading ? 'default' : 'pointer',
          opacity: loading ? 0.7 : 1,
          maxWidth: 320,
          px: 1.5,
          py: 1,
          borderRadius: '11px',
          color: '#fff',
          fontWeight: 600,
          fontSize: '0.85rem',
          bgcolor: 'rgba(255,255,255,0.15)',
          border: '1px solid rgba(255,255,255,0.24)',
          transition: 'background .15s',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.26)' },
        }}
      >
        {loading
          ? <CircularProgress size={15} sx={{ color: '#fff' }} />
          : <StorefrontIcon sx={{ fontSize: 17, opacity: 0.9 }} />}
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Box>
        <KeyboardArrowDownIcon sx={{ fontSize: 18, opacity: 0.85 }} />
      </Box>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 420, minWidth: 280 } } }}
      >
        <MenuItem
          selected={store === NO_STORE}
          onClick={() => choose(NO_STORE)}
          sx={{ fontStyle: 'italic', color: 'text.secondary' }}
        >
          <ListItemText>Select</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          selected={store === ALL_STORES}
          onClick={() => choose(ALL_STORES)}
          sx={{ fontWeight: 700 }}
        >
          <ListItemText>All stores</ListItemText>
        </MenuItem>
        <Divider />
        {storesStatus === 'error' && (
          <MenuItem onClick={() => requestStores(true)} sx={{ color: 'error.main' }}>
            <ListItemText>Couldn&apos;t load stores — tap to retry</ListItemText>
          </MenuItem>
        )}
        {storesStatus === 'ready' && stores.length === 0 && (
          <MenuItem disabled>
            <ListItemText>No stores available</ListItemText>
          </MenuItem>
        )}
        {stores.map((s) => (
          <MenuItem key={s.externalId} selected={store === s.externalId} onClick={() => choose(s.externalId)}>
            <ListItemText>{s.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
